import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLocation } from 'wouter';
import { ArrowLeft } from 'lucide-react';
import { COMPANY } from '@/config';

export default function TermsAndConditions() {
  const [ , navigate ] = useLocation();

  return (
    <div className="min-h-screen bg-[#0E1525]">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-2xl border border-slate-700 bg-[#1E293B]">
            <CardHeader className="text-center space-y-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 text-gray-400 hover:text-white hover:bg-slate-700"
                >
                  <ArrowLeft className="h-4 w-4"/>
                  Back
                </Button>
                <div className="flex-1"/>
              </div>
              <CardTitle className="text-3xl font-bold text-white">
                Terms and Conditions of Service
              </CardTitle>
              <p className="text-sm text-gray-400">
                Last Updated: December 2024
              </p>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[70vh] pr-4">
                <div className="space-y-6 text-gray-300">
                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance of Terms</h2>
                    <p>
                      By accessing or using our video chat service (&#34;Service&#34;), you agree to be bound by these Terms and
                      Conditions (&#34;Terms&#34;). If you do not agree to these Terms, you may not use our Service.
                    </p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">2. Service Description</h2>
                    <p>Our Service provides a real-time video chat platform that connects users randomly through WebRTC
                      technology. The platform includes:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Peer-to-peer video communication</li>
                      <li>Random user matching</li>
                      <li>Text chat functionality</li>
                      <li>Desktop and laptop computer compatibility only</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">3. System Requirements and Platform
                      Support</h2>
                    <h3 className="text-lg font-medium text-gray-200 mb-2">Desktop/Laptop Required:</h3>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>This service is designed exclusively for desktop and laptop computers</li>
                      <li>Mobile devices (smartphones, tablets) are not supported</li>
                      <li>Minimum screen width requirements apply</li>
                      <li>Compatible browsers: Chrome 80+, Firefox 75+, Safari 14+, Edge 80+</li>
                      <li>Camera and microphone access required</li>
                      <li>Stable internet connection required</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">4. Age Requirements and Eligibility</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Users must be at least 18 years old</li>
                      <li>Users under 18 are prohibited from using the Service</li>
                      <li>By using the Service, you represent that you meet these age requirements</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">5. User Conduct and Prohibited Activities</h2>
                    <p>You agree NOT to:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Share or display explicit, inappropriate, or offensive content</li>
                      <li>Engage in harassment, bullying, or threatening behavior</li>
                      <li>Share personal information (addresses, phone numbers, social media accounts)</li>
                      <li>Attempt to record, screenshot, or capture video calls without consent</li>
                      <li>Use the Service for commercial solicitation or advertising</li>
                      <li>Impersonate others or provide false information</li>
                      <li>Use automated systems, bots, or scripts</li>
                      <li>Engage in illegal activities or promote violence</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">6. Content Moderation and Safety</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Users are responsible for monitoring their own behavior and content</li>
                      <li>We provide reporting tools for users to flag inappropriate behavior</li>
                      <li>Reported violations are reviewed and may result in account suspension</li>
                      <li>We reserve the right to immediately terminate sessions based on user reports</li>
                      <li>Repeated violations result in permanent account suspension</li>
                      <li>We cooperate with law enforcement when required by law</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">7. Privacy and Data Protection</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Video calls are peer-to-peer and not stored on our servers</li>
                      <li>We collect minimal data necessary for service functionality</li>
                      <li>Location data is used only for display purposes (showing country flags)</li>
                      <li>We do not sell personal information to third parties</li>
                      <li>See our Privacy Policy for detailed information handling practices</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">8. Technical Requirements</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Desktop or laptop computer required</li>
                      <li>Compatible modern browser with WebRTC support</li>
                      <li>Stable internet connection</li>
                      <li>Camera and microphone access permissions</li>
                      <li>Minimum screen resolution and width requirements</li>
                      <li>The Service operates on a best-effort basis with no uptime guarantees</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">9. Intellectual Property</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>You retain ownership of content you create</li>
                      <li>You grant us license to use your content for service operation</li>
                      <li>Our platform and code remain our intellectual property</li>
                      <li>Users may not reverse engineer or copy our technology</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">10. Disclaimers and Limitation of
                      Liability</h2>
                    <p>The Service is provided &#34;AS IS&#34; without warranties of any kind.</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>We are not responsible for user behavior or content shared by others</li>
                      <li>We do not guarantee continuous, error-free service operation</li>
                      <li>Users interact at their own risk and are responsible for their safety</li>
                      <li>We are not liable for any damages arising from Service use</li>
                      <li>Our liability is limited to the maximum extent permitted by law</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">11. Service Availability and
                      Modifications</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>We may modify, suspend, or discontinue the Service at any time</li>
                      <li>Features may be added, removed, or changed without notice</li>
                      <li>We reserve the right to limit usage or implement new restrictions</li>
                      <li>No compensation is provided for service interruptions</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">12. Account Termination</h2>
                    <p>We may terminate access for:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Violation of these Terms</li>
                      <li>Inappropriate behavior or content reported by users</li>
                      <li>Suspected illegal activity</li>
                      <li>Technical abuse or system manipulation</li>
                      <li>Using unsupported devices (mobile phones, tablets)</li>
                    </ul>
                    <p className="mt-2">Users may delete their account at any time through account settings.</p>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">13. Geographic Restrictions</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Service availability varies by location</li>
                      <li>Users are responsible for compliance with local laws</li>
                      <li>We may block access from certain jurisdictions</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">14. Third-Party Services</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>We may integrate with external authentication providers</li>
                      <li>Third-party services have separate terms and privacy policies</li>
                      <li>We are not responsible for third-party service failures</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">15. Dispute Resolution</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>Disputes will be resolved through binding arbitration</li>
                      <li>Class action lawsuits are waived</li>
                      <li>Local jurisdiction law governs these Terms</li>
                      <li>Legal proceedings must be filed within one year of the dispute</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">16. Updates to Terms</h2>
                    <ul className="list-disc pl-6 space-y-1">
                      <li>We may update these Terms at any time</li>
                      <li>Continued use constitutes acceptance of updated Terms</li>
                      <li>Material changes will be communicated through the platform</li>
                    </ul>
                  </section>

                  <section>
                    <h2 className="text-xl font-semibold text-white mb-3">17. Contact Information</h2>
                    <p>For questions about these Terms, contact us at:</p>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Email: {COMPANY.supportEmail}</li>
                    </ul>
                  </section>

                  <div className="mt-8 p-4 bg-slate-800 rounded-lg">
                    <p className="text-sm text-gray-300 font-medium">
                      By using our Service, you acknowledge that you have read, understood, and agree to be bound by
                      these Terms and Conditions.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
